import { type FC, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Alert, Button } from "@mantine/core";
import { addDays, isAfter } from "date-fns";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { AbsoluteRoute } from "../../../core/routes/absoluteRoutes";
import { CofConfigId } from "../../../gql-generated/types";
import { TosModalHeader } from "../../../lens-creator-payout/components/messages.generated";
import { useMonetizationTos } from "../../hooks";

import { payoutTosBanner } from "./PayoutTosBanner.module.css";
import { TosModal } from "./TosModal";
import { TOS_KEY_MAP, TosSource } from "./constants";
import { TosBannerMessage, TosBannerViewTerms } from "./messages.generated";

const DISMISS_DURATION_DAYS = 30;

const dismissExpiryAtom = atomWithStorage<number | null>("payout-tos-banner-dismiss-expiry", null);

export const PayoutTosBanner: FC = () => {
    const { pathname } = useLocation();
    const { configs } = useFeatureGates();
    const isCreatorPayoutEnabled = configs[CofConfigId.MyLensesCreatorPayout].enabled;
    const dismissible = pathname.startsWith(AbsoluteRoute.HOME);
    const [dismissExpiry, setDismissExpiry] = useAtom(dismissExpiryAtom);
    const dismissed = dismissExpiry !== null && isAfter(dismissExpiry, new Date());

    const {
        loading: tosLoading,
        tos,
        acceptedLatestVersion,
        tosModalVisible,
        tosSource,
        triggerTosModal,
        closeTosModal,
        triggerTosAcceptance,
    } = useMonetizationTos();

    const handleTosAcceptance = useCallback(async () => {
        try {
            await triggerTosAcceptance();
        } catch (error) {
            console.error("Failed to accept TOS:", error);
        }
    }, [triggerTosAcceptance]);

    if (!isCreatorPayoutEnabled || acceptedLatestVersion || (dismissible && dismissed)) {
        return null;
    }

    return (
        <>
            <Alert
                className={payoutTosBanner}
                p="xs"
                radius={0}
                withCloseButton={dismissible}
                onClose={() => {
                    setDismissExpiry(addDays(new Date(), DISMISS_DURATION_DAYS).getTime());
                }}
            >
                <span className="paragraph-p200">
                    <TosBannerMessage />
                </span>{" "}
                <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => {
                        triggerTosModal(TosSource.CreatorPayout);
                    }}
                    loading={tosLoading}
                >
                    <TosBannerViewTerms />
                </Button>
            </Alert>
            <TosModal
                tosHeading={<TosModalHeader />}
                tosMetadata={tos?.metadata ?? undefined}
                visible={tosModalVisible}
                disabled={tosLoading}
                loading={tosLoading}
                accepted={acceptedLatestVersion}
                onAccept={handleTosAcceptance}
                onDismiss={closeTosModal}
                initialStepKey={tosSource ? TOS_KEY_MAP[tosSource] : undefined}
            />
        </>
    );
};
